import React, { useState, useRef, useEffect } from "react";
import { UploadCloud, FileText, CheckCircle2, Download, FileSpreadsheet, Plus, Compass, AlertTriangle, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Spinner, Select, Modal } from "../components/ui";

export default function Upload() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState("");
  const [newSourceOpen, setNewSourceOpen] = useState(false);
  const inputRef = useRef(null);

  const loadSources = () => api.get("/clients/sources").then((r) => setSources(r.data)).catch(() => {});
  useEffect(() => { loadSources(); }, []);

  const submit = async () => {
    if (!file) return toast("Choose a CSV or Excel file first", "error");
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    if (source) fd.append("source", source);
    try {
      const r = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.inserted > 0) {
        toast(r.data.skipped_total ? `Imported ${r.data.inserted} leads, ${r.data.skipped_total} skipped` : `Imported ${r.data.inserted} leads`);
      } else if (r.data.skipped_total > 0) {
        toast(`No leads imported — ${r.data.skipped_total} row(s) skipped`, "error");
      } else {
        toast("No leads found in file", "error");
      }
      setFile(null);
    } catch (e) { toast(e.response?.data?.detail || "Import failed", "error"); }
    finally { setBusy(false); }
  };

  const sample = "name,email,phone,country,source\nJohn Doe,john@example.com,447400000000,GB,Facebook\nJane Smith,jane@example.com,4915112345678,DE,Google";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white">Upload Data</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Import clients from CSV or Excel, or add them manually</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File upload */}
        <div className="card p-6">
          <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-indigo-500" /> File Upload</h3>
          <p className="text-xs text-slate-400 mt-1 mb-4">Upload a CSV or Excel file with name, email, and phone columns</p>
          <div className="mb-4">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Source (optional)</label>
            <div className="flex gap-2 mt-1">
              <div className="flex-1 min-w-0">
                <Select value={source} onChange={setSource} placeholder="Select an existing source..." options={sources.map((s) => ({ value: s.name, label: s.name + (s.count ? ` (${s.count})` : "") }))} testid="upload-source-select" />
              </div>
              <button className="btn-secondary px-3 py-2 shrink-0" onClick={() => setNewSourceOpen(true)} data-testid="upload-new-source-btn"><Plus className="w-4 h-4" /> + New</button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Applied to every lead in this file. Pick an existing source or create a new one.</p>
          </div>
          <div
            className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 text-center cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
            data-testid="upload-dropzone"
          >
            <UploadCloud className="w-10 h-10 mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">Click or drag to upload</p>
            <p className="text-xs text-slate-400 mt-1">Supports .csv, .xlsx, .xls</p>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
          </div>
          {file && (
            <div className="mt-4 flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <FileText className="w-5 h-5 text-indigo-500" />
              <span className="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate">{file.name}</span>
              <button className="btn-primary px-3 py-1.5" onClick={submit} disabled={busy} data-testid="upload-submit">{busy ? <Spinner className="w-4 h-4" /> : "Import"}</button>
            </div>
          )}
          <a className="btn-secondary w-full justify-center mt-4" href={`data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`} download="leads_template.csv" data-testid="download-sample"><Download className="w-4 h-4" /> Download CSV Template</a>
          {result && <ImportResult result={result} />}
        </div>

        {/* Manual add */}
        <ManualAdd sources={sources} />
      </div>

      {user?.is_admin && <SourcesPanel sources={sources} onChanged={loadSources} />}

      {newSourceOpen && <NewSourceModal onClose={() => setNewSourceOpen(false)} onDone={(name) => { setNewSourceOpen(false); setSource(name); loadSources(); }} />}
    </div>
  );
}

function NewSourceModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const value = name.trim();
    if (!value) return toast("Source name is required", "error");
    setBusy(true);
    try {
      await api.post("/clients/sources", { name: value });
      toast("Source created");
      onDone(value);
    } catch (e) {
      toast(e.response?.data?.detail || "Failed to create source", "error");
      if (e.response?.status === 409) { setName(""); return; }
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New Source" testid="new-source-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy || !name.trim()} data-testid="new-source-save">Save</button></>}>
      <div>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Source name</label>
        <input className="input mt-1" placeholder="e.g. Facebook" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-source-input" />
      </div>
    </Modal>
  );
}

function humanizeReason(s) {
  if (s.reason === "duplicate_email") return s.matched_name ? `Duplicate email — already in CRM: ${s.matched_name}` : "Duplicate email";
  if (s.reason === "duplicate_phone") return s.matched_name ? `Duplicate phone — already in CRM: ${s.matched_name}` : "Duplicate phone";
  if (s.reason === "duplicate_email_in_file" || s.reason === "duplicate_phone_in_file") return "Duplicate in this file";
  if (s.reason === "missing_name") return "No name";
  return s.reason;
}

function ImportResult({ result }) {
  return (
    <div data-testid="upload-result">
      {result.inserted > 0 ? (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="w-5 h-5" /> Imported {result.inserted} of {result.total_rows} leads.
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-5 h-5" /> No leads imported — {result.skipped_total} row(s) skipped.
        </div>
      )}
      {result.skipped_total > 0 && (
        <div className="mt-3 card p-4">
          <div className="text-sm font-semibold font-display text-slate-900 dark:text-white mb-2">Skipped {result.skipped_total} lead(s)</div>
          <div className="max-h-64 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800" data-testid="upload-skipped-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="p-2.5 text-left">Row</th>
                  <th className="p-2.5 text-left">Name</th>
                  <th className="p-2.5 text-left">Contact</th>
                  <th className="p-2.5 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(result.skipped || []).map((s, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800/50 last:border-b-0">
                    <td className="p-2.5 text-slate-400">{s.row}</td>
                    <td className="p-2.5 text-slate-700 dark:text-slate-200">{s.name || <span className="text-slate-300">—</span>}</td>
                    <td className="p-2.5 text-slate-600 dark:text-slate-300">{s.email || s.phone || <span className="text-slate-300">—</span>}</td>
                    <td className="p-2.5 text-slate-600 dark:text-slate-300">{humanizeReason(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {result.unrecognized_columns?.length > 0 && (
        <p className="mt-2 text-xs text-slate-400">Ignored unrecognized column(s): {result.unrecognized_columns.join(", ")}</p>
      )}
    </div>
  );
}

function SourcesPanel({ sources, onChanged }) {
  const [busy, setBusy] = useState({});
  const del = async (name) => {
    if (!window.confirm(`Delete source "${name}"?`)) return;
    setBusy((b) => ({ ...b, [name]: true }));
    try {
      await api.delete("/clients/sources/" + encodeURIComponent(name));
      toast("Source deleted");
      onChanged();
    } catch (e) {
      toast(e.response?.data?.detail || "Failed to delete source", "error");
    } finally { setBusy((b) => ({ ...b, [name]: false })); }
  };
  return (
    <div className="card p-6">
      <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2"><Compass className="w-5 h-5 text-indigo-500" /> Sources</h3>
      <p className="text-xs text-slate-400 mt-1 mb-4">Built-in Default plus any sources you create. Delete only when no leads use a source.</p>
      <div className="space-y-2">
        {sources.map((s) => (
          <div key={s.name} className="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2.5">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100 flex-1">{s.name}</span>
            {s.name === "Default" && <span className="badge bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">built-in</span>}
            <span className="badge bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-800">{s.count}</span>
            {s.name !== "Default" && (
              <button className="btn-ghost p-1.5 text-rose-500 dark:text-rose-400" disabled={!!busy[s.name]} onClick={() => del(s.name)} data-testid={"source-delete-" + s.name} aria-label={`Delete ${s.name}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ManualAdd({ sources }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", client_type: "lead", source: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast("Name is required", "error");
    setBusy(true);
    try {
      await api.post("/clients/manual-add", { ...form, source: form.source || undefined });
      toast(`${form.client_type === "lead" ? "Lead" : "Client"} added`);
      setForm({ name: "", email: "", phone: "", country: "", client_type: form.client_type, source: "" });
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <div className="card p-6">
      <h3 className="font-semibold font-display text-slate-900 dark:text-white flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-500" /> Add Manually</h3>
      <p className="text-xs text-slate-400 mt-1 mb-4">Add a single client or lead manually</p>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Name *</label>
          <input className="input mt-1" placeholder="John Doe" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="manual-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
          <input className="input mt-1" placeholder="john@example.com" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="manual-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Phone</label>
            <input className="input mt-1" placeholder="+1234567890" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="manual-phone" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Country</label>
            <input className="input mt-1" placeholder="GB" value={form.country} onChange={(e) => upd("country", e.target.value.toUpperCase())} maxLength={2} data-testid="manual-country" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Source</label>
          <Select value={form.source} onChange={(v) => upd("source", v)} placeholder="Source (optional)" options={sources.map((s) => ({ value: s.name, label: s.name + (s.count ? ` (${s.count})` : "") }))} testid="manual-source-select" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Type</label>
          <Select value={form.client_type} onChange={(v) => upd("client_type", v)} options={[{ value: "lead", label: "Lead" }, { value: "client", label: "Client" }]} testid="manual-type" />
        </div>
        <button className="btn-primary w-full" onClick={submit} disabled={busy} data-testid="manual-submit">{busy ? <Spinner className="w-4 h-4" /> : `Add ${form.client_type === "lead" ? "Lead" : "Client"}`}</button>
      </div>
    </div>
  );
}
